import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';
import { Tool } from '../models/tool.models';

export interface ToolConfig {
  id: string;
  displayOrder: number;
  isAvailable: boolean;
  customDescription?: string;
  customName?: string;
  toolDomain?: string; // Renamed from productDomain to toolDomain
  toolName?: string; // Renamed from productName to toolName
}

export interface ToolConfigData {
  [toolId: string]: ToolConfig;
}

@Injectable({
  providedIn: 'root'
})
export class ToolConfigService {
  readonly configLoaded$ = new BehaviorSubject<boolean>(true);
  private authService: AuthService | null = null;

  constructor(private injector: Injector) {
    // Configuration is now loaded from tools, so it's always available
    this.configLoaded$.next(true);
  }

  /**
   * Get AuthService lazily to avoid circular dependency
   */
  private getAuthService(): AuthService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    return this.authService;
  }

  /**
   * Get configuration for a specific tool from tools data
   */
  private getToolConfigFromTool(tool: Tool | null): ToolConfig {
    if (!tool) {
      return this.createDefaultConfig('');
    }
    
    return {
      id: tool.id,
      displayOrder: tool.tool_order ?? (parseInt(tool.id) || 999),
      isAvailable: tool.isAvailable === '1',
      customName: tool.tool_name, // Use tool_name from API
      customDescription: tool.description,
      toolDomain: tool.tool_domain || tool.product_domain, // Use new API field, fallback to legacy
      toolName: tool.tool_key ?? tool.product_key ?? tool.name // Identifier for filter type (dm, pm, km)
    };
  }

  /**
   * Get filtered and sorted tools based on configuration
   * All tools are now visible, regardless of isAvailable status
   * Tools are sorted by tool_order (from database), falling back to id for backward compatibility
   */
  getFilteredTools(tools: { [key: string]: Tool }): Tool[] {
    if (!tools) return [];

    return Object.values(tools)
      .sort((a, b) => {
        // Use tool_order if available, otherwise fall back to id for backward compatibility
        const orderA = a.tool_order ?? (parseInt(a.id) || 999);
        const orderB = b.tool_order ?? (parseInt(b.id) || 999);
        return orderA - orderB;
      });
  }

  /**
   * Get tool display name (custom or original)
   */
  getToolDisplayName(tool: Tool): string {
    return tool.tool_name || tool.name || ''; // tool_name is the display name
  }

  /**
   * Get tool display description (custom or original)
   */
  getToolDisplayDescription(tool: Tool): string {
    return tool.description || "No description available";
  }


  /**
   * Check if configuration is loaded
   */
  isConfigLoaded(): boolean {
    return true; // Configuration is always available from tools
  }

  /**
   * Resolve tool by tool_key, product_key, shortname, or id (for backward compatibility)
   */
  private findTool(toolIdOrKey: string): Tool | undefined {
    const tools = this.getAuthService().getTools();
    if (!tools) return undefined;
    const key = toolIdOrKey?.toLowerCase?.() ?? toolIdOrKey;
    return (
      Object.values(tools).find(t => (t.tool_key ?? t.product_key)?.toLowerCase() === key) ??
      Object.values(tools).find(t => t.shortname?.toLowerCase() === key) ??
      tools[toolIdOrKey]
    );
  }

  /**
   * Get configuration for a specific tool (with fallback)
   */
  private getToolConfigSync(toolIdOrShortname: string): ToolConfig {
    const tool = this.findTool(toolIdOrShortname);
    return this.getToolConfigFromTool(tool ?? null);
  }

  private createDefaultConfig(toolId: string): ToolConfig {
    return {
      id: toolId,
      displayOrder: 999, // Default to end
      isAvailable: true // Default to available
    };
  }

  /**
   * Check if a tool is available
   */
  isToolAvailable(toolIdOrKey: string): boolean {
    const tool = this.findTool(toolIdOrKey);
    if (!tool) {
      return true; // Default to available if tool not found
    }
    return tool.isAvailable === '1';
  }

  /**
   * Get tool domain for a specific tool
   */
  getToolDomain(toolIdOrKey: string): string {
    const tool = this.findTool(toolIdOrKey);
    return tool?.tool_domain || tool?.product_domain || '';
  }

  /**
   * Get tool display name for a specific tool (by id, tool_key, or shortname)
   */
  getToolName(toolIdOrKey: string): string {
    const tool = this.findTool(toolIdOrKey);
    return tool ? (tool.tool_name || tool.name || toolIdOrKey) : toolIdOrKey;
  }

  /**
   * Get all tool configurations (for compatibility)
   * Returns configurations based on tools data
   */
  getAllConfigs(): ToolConfigData {
    const tools = this.getAuthService().getTools();
    if (!tools) {
      return {};
    }
    const configs: ToolConfigData = {};
    Object.values(tools).forEach(tool => {
      const key = tool.tool_key ?? tool.product_key ?? tool.shortname;
      if (key) {
        configs[key] = this.getToolConfigFromTool(tool);
      }
    });
    return configs;
  }

}
